import type { DesktopMenuAction } from "@afar-research/contracts";
import { browserBuildEnvironment, isElectron } from "./env";
import { isRuntimeWebBuild } from "./environments/primary/browserEntry.logic";
import { isMacPlatform } from "./lib/utils";

const MACOS_TRAFFIC_LIGHTS_LEFT_INSET = "90px";

export type SupportedClientSurface = "desktop" | "host-web" | "runtime-web";

export interface WorkspaceShellPlatformAdapter {
  readonly clientSurface: SupportedClientSurface;
  readonly controlsLeftInset: string | null;
  /** Desktop application-menu items that act on the shared shell. Inert on the web. */
  readonly subscribeMenuAction: (action: DesktopMenuAction, listener: () => void) => () => void;
}

/**
 * Which surface this is. A browser page served by an Environment is its Host Web Client wherever
 * the page was opened from — loopback, or a network address the service listens on — because the
 * Environment served it and paired it. Only a build made for a Runtime's relay is Runtime Web.
 */
export function resolveSupportedClientSurface(input: {
  readonly isDesktop: boolean;
  readonly isRuntimeWebBuild: boolean;
}): SupportedClientSurface {
  if (input.isDesktop) return "desktop";
  return input.isRuntimeWebBuild ? "runtime-web" : "host-web";
}

/**
 * Isolates native-window capabilities from the shared React shell. Web
 * surfaces receive the same adapter shape with inert native facilities.
 */
export function createWorkspaceShellPlatformAdapter(): WorkspaceShellPlatformAdapter {
  const browserWindow = typeof window === "undefined" ? null : window;
  const platform = typeof navigator === "undefined" ? "" : navigator.platform;
  const menuAction = browserWindow?.desktopBridge?.onMenuAction;

  return {
    clientSurface: resolveSupportedClientSurface({
      isDesktop: isElectron,
      isRuntimeWebBuild: isRuntimeWebBuild(browserBuildEnvironment),
    }),
    controlsLeftInset:
      isElectron && isMacPlatform(platform) ? MACOS_TRAFFIC_LIGHTS_LEFT_INSET : null,
    subscribeMenuAction(action, listener) {
      if (typeof menuAction !== "function") return () => undefined;
      const unsubscribe = menuAction((dispatched) => {
        if (dispatched === action) listener();
      });
      return () => unsubscribe?.();
    },
  };
}
